import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { isApiError } from '@/api/errors';
import { labelFrom } from '@/app/breadcrumbs';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import {
  awardQuery,
  awardsListQuery,
  awardsSearchSchema,
  campaignQuery,
  campaignsListQuery,
  campaignsSearchSchema,
  type AwardKind,
} from './api';
import { PromotionNotFound } from './pages/promotion-not-found';

/** `$campaignId` as a positive integer, or a not-found. */
export function parseCampaignId(raw: string): number {
  const id = Number(raw);
  if (!Number.isInteger(id) || id <= 0) throw notFound();
  return id;
}

async function ensureOrNotFound<T>(load: () => Promise<T>) {
  try {
    return await load();
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') throw notFound();
    throw error;
  }
}

const loadCampaign = (context: RouterContext, raw: string) => {
  const id = parseCampaignId(raw);
  return ensureOrNotFound(() => context.queryClient.ensureQueryData(campaignQuery(id)));
};

const loadAward = (context: RouterContext, kind: AwardKind, uuid: string) =>
  ensureOrNotFound(() => context.queryClient.ensureQueryData(awardQuery(kind, uuid)));

export const campaignsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'campaigns',
  validateSearch: campaignsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_PROMOTIONS'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(campaignsListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/campaigns-list-page'), 'CampaignsListPage'),
});

export const campaignCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'campaigns/new',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PROMOTIONS', 'UPDATE_PROMOTIONS'),
  staticData: { crumbs: () => [{ label: 'New campaign' }] },
  component: lazyRouteComponent(() => import('./pages/campaign-form-page'), 'CampaignCreatePage'),
});

export const campaignDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'campaigns/$campaignId',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PROMOTIONS'),
  loader: ({ context, params }) => loadCampaign(context, params.campaignId),
  staticData: {
    crumbs: (params, campaign) => [{ label: labelFrom(campaign, 'Name') ?? `Campaign ${params.campaignId ?? ''}` }],
  },
  notFoundComponent: PromotionNotFound,
  component: lazyRouteComponent(() => import('./pages/campaign-detail-page'), 'CampaignDetailPage'),
});

export const campaignEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'campaigns/$campaignId/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PROMOTIONS', 'UPDATE_PROMOTIONS'),
  loader: ({ context, params }) => loadCampaign(context, params.campaignId),
  staticData: {
    crumbs: (params, campaign) => [
      {
        label: labelFrom(campaign, 'Name') ?? `Campaign ${params.campaignId ?? ''}`,
        href: `/campaigns/${params.campaignId ?? ''}`,
      },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: PromotionNotFound,
  component: lazyRouteComponent(() => import('./pages/campaign-form-page'), 'CampaignEditPage'),
});

const awardsRoute = (kind: AwardKind) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: kind === 'active' ? 'awards/active' : 'awards/finished',
    validateSearch: awardsSearchSchema,
    beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_PROMOTIONS'),
    loaderDeps: ({ search }) => search,
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchQuery(awardsListQuery(kind, deps));
    },
    component: lazyRouteComponent(
      () => import('./pages/awards-list-page'),
      kind === 'active' ? 'ActiveAwardsPage' : 'FinishedAwardsPage',
    ),
  });

export const activeAwardsRoute = awardsRoute('active');
export const finishedAwardsRoute = awardsRoute('finished');

export const activeAwardDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'awards/active/$uuid',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PROMOTIONS'),
  loader: ({ context, params }) => loadAward(context, 'active', params.uuid),
  staticData: { crumbs: (params) => [{ label: `Award ${(params.uuid ?? '').slice(0, 8)}` }] },
  notFoundComponent: PromotionNotFound,
  component: lazyRouteComponent(() => import('./pages/award-detail-page'), 'ActiveAwardDetailPage'),
});

export const finishedAwardDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'awards/finished/$uuid',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_PROMOTIONS'),
  loader: ({ context, params }) => loadAward(context, 'finished', params.uuid),
  staticData: { crumbs: (params) => [{ label: `Award ${(params.uuid ?? '').slice(0, 8)}` }] },
  notFoundComponent: PromotionNotFound,
  component: lazyRouteComponent(() => import('./pages/award-detail-page'), 'FinishedAwardDetailPage'),
});

export const promotionsRoutes = [
  campaignsListRoute,
  campaignCreateRoute,
  campaignDetailRoute,
  campaignEditRoute,
  activeAwardsRoute,
  finishedAwardsRoute,
  activeAwardDetailRoute,
  finishedAwardDetailRoute,
] as const;
